'use client';

import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import TickerPopup from './TickerPopup';

interface PopupState { ticker: string; anchor: DOMRect; hover: boolean }

interface TickerPopupCtxValue {
  /** Open immediately (click surfaces). Persists until close()/Esc. */
  open: (ticker: string, anchor: DOMRect) => void;
  close: () => void;
  /** Hover-intent open: shows after a short dwell so quickly skimming
   *  across tickers doesn't spam popups; the popup then stays open while the
   *  cursor is over EITHER the trigger or the popup itself. */
  hoverOpen: (ticker: string, anchor: DOMRect) => void;
  /** Hover-intent close: cancels a pending open, then closes after a grace
   *  period (so moving from the trigger to the popup doesn't drop it). */
  hoverClose: () => void;
}

const NOOP_CTX: TickerPopupCtxValue = { open: () => {}, close: () => {}, hoverOpen: () => {}, hoverClose: () => {} };
const Ctx = createContext<TickerPopupCtxValue>(NOOP_CTX);

const HOVER_OPEN_DELAY_MS = 500;  // dwell before a hover first shows the popup
const HOVER_SWITCH_DELAY_MS = 120; // faster switch once a popup is already up
const HOVER_CLOSE_DELAY_MS = 280; // grace period covering the trigger→popup gap

/** Singleton popup provider. Wraps the dashboard so any ticker surface
 *  (TickerLink click, or a hovered chip) can open a price/change/1d-chart
 *  card without each widget re-implementing the popup state machine. */
export function TickerPopupProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PopupState | null>(null);
  const openTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Mirror the open/hover state into refs so the timer callbacks read fresh
  // values without being re-created on every state change.
  const isOpenRef = useRef(false);
  const isHoverRef = useRef(false);
  isOpenRef.current = state !== null;
  isHoverRef.current = state?.hover ?? false;

  const clearOpen = () => { if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; } };
  const clearClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };

  // Switch tickers WITHOUT remounting (keep the original anchor so the
  // popup's mount-time position isn't recomputed; only the ticker changes).
  const show = (ticker: string, anchor: DOMRect, hover: boolean) =>
    setState((prev) => prev ? { ...prev, ticker, hover } : { ticker, anchor, hover });

  const open = useCallback((ticker: string, anchor: DOMRect) => {
    clearOpen(); clearClose();
    show(ticker, anchor, false);
  }, []);
  const close = useCallback(() => { clearOpen(); clearClose(); setState(null); }, []);

  const hoverOpen = useCallback((ticker: string, anchor: DOMRect) => {
    clearClose();          // moving onto a (new) trigger cancels a pending close
    clearOpen();
    const delay = isOpenRef.current ? HOVER_SWITCH_DELAY_MS : HOVER_OPEN_DELAY_MS;
    openTimer.current = setTimeout(() => { openTimer.current = null; show(ticker, anchor, true); }, delay);
  }, []);

  const hoverClose = useCallback(() => {
    clearOpen();           // cancel a pending open if the cursor left before it fired
    clearClose();
    closeTimer.current = setTimeout(() => { closeTimer.current = null; setState(null); }, HOVER_CLOSE_DELAY_MS);
  }, []);

  // Cursor entered the popup itself → keep it open (cancel the pending close).
  const cancelClose = useCallback(() => { clearClose(); }, []);

  useEffect(() => () => { clearOpen(); clearClose(); }, []);

  return (
    <Ctx.Provider value={{ open, close, hoverOpen, hoverClose }}>
      {children}
      {state && (
        <TickerPopup
          ticker={state.ticker}
          anchor={state.anchor}
          onClose={close}
          // Only hover-opened popups auto-close on leave — a click-opened
          // popup must persist regardless of pointer position.
          onMouseEnter={state.hover ? cancelClose : undefined}
          onMouseLeave={state.hover ? hoverClose : undefined}
        />
      )}
    </Ctx.Provider>
  );
}

/** Returns the no-op shim (NOOP_CTX) when used outside the provider; widgets
 *  that may render outside the dashboard should fall back to plain navigation.
 *  Typed `| null` for back-compat with existing optional-chaining call sites. */
export function useTickerPopup(): TickerPopupCtxValue | null {
  return useContext(Ctx);
}
